import { useQueryClient } from '@tanstack/react-query'
import { isEmpty } from 'lodash'
import { toast } from 'react-toastify'

import { UNREGISTERED_SIGN_IN_MESSAGE } from '~/features/auth/constants'
import { TSessionDetails } from '~/features/auth/models'
import { queries } from '~/features/auth/utils'
import useCreateUpdateUser from '~/features/user/hooks/useUpdateUser'
import { TUser } from '~/features/user/models'
import { queries as userQueries } from '~/features/user/utils'
import { EHttpErrorName } from '~/models/api'
import { HttpServiceBaseResponse } from '~/services/http/models'
import { store } from '~/store'

const useCompleteSignIn = () => {
  const [setUser, impersonatorsUserId, currentUserId] = store((state) => [
    state.setUser,
    state.user.impersonatorsUserId,
    state.user.id,
  ])

  const queryClient = useQueryClient()

  const { mutate: updateUser } = useCreateUpdateUser()

  /**
   * Loads the app user from the database and updates last login.
   * Self-registration is disabled; prospects should book a demo, existing
   * customers enter via admin invite.
   * On failure: toast and throw — Hub `signedIn` `.catch` owns the single
   * hard logout (avoids double sign-out with a stale isSigningOut guard).
   */
  const createOrUpdateUser = async (
    currentAuthSession: TSessionDetails | undefined,
  ): Promise<TSessionDetails> => {
    const failSignIn = (message: string): never => {
      toast.error(message)
      throw new Error(message)
    }

    let authSession = currentAuthSession

    if (!authSession?.cognitoUid) {
      try {
        authSession = await queryClient.fetchQuery({
          ...queries.authSession(),
          staleTime: 0,
        })
      } catch {
        // eslint-disable-next-line no-console
        console.error('An error occurred while fetching auth session.', 3)
        return failSignIn('An error occurred while fetching auth session.')
      }
    }

    if (!authSession?.cognitoUid) {
      // eslint-disable-next-line no-console
      console.error('No cognitoUid found in auth session', 4)
      return failSignIn('No cognitoUid found in auth session')
    }

    let userData: HttpServiceBaseResponse<TUser>
    try {
      userData = await queryClient.fetchQuery(
        userQueries.userCognito(authSession.cognitoUid),
      )
    } catch (e: unknown) {
      if ((e as Error).name === EHttpErrorName.userNotFound) {
        return failSignIn(UNREGISTERED_SIGN_IN_MESSAGE)
      }
      // eslint-disable-next-line no-console
      console.error('An error occurred while fetching user data.', 6)
      return failSignIn('An error occurred while fetching user data.')
    }

    if (isEmpty(userData.data)) {
      // eslint-disable-next-line no-console
      console.error('An error occurred while fetching user data.', 7)
      return failSignIn('An error occurred while fetching user data.')
    }

    const user = userData.data

    updateUser({
      params: {
        id: user.id,
        lastLogin: new Date().toISOString(),
      },
    })

    if (
      user.id &&
      user.id !== impersonatorsUserId &&
      user.id !== currentUserId
    ) {
      setUser({
        id: user.id,
        organizationId: user.orgId,
      })
    }

    return authSession
  }

  return { completeSignIn: createOrUpdateUser }
}

export default useCompleteSignIn
